import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Loading from "components/common/Loading";
import { useAdminListAllAdvisors } from "services/admin";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import { MailCheck, Map as MapIcon, Plus } from "lucide-react";

import { Advisor, AdvisorStatus, FamilyStub } from "protogen/common_pb";
import {
  getFormattedLocation,
  getLatLng,
  getSearchableLocation,
} from "components/family/utils";
import MapList from "components/common/maps/MapList";
import { Treatment } from "components/common/maps/FMarker";
import SearchBar from "components/forum/SearchBar";
import AddAdvisorDialog from "components/advisor/AddAdvisorDialog";
import EditAdvisorDialog from "components/advisor/EditAdvisorDialog";
import InviteAdvisorDialog from "components/advisor/InviteAdvisorDialog";
import AdvisorCard from "components/admin/AdvisorCard";

const getStateColor = (status: AdvisorStatus) => {
  switch (status) {
    case AdvisorStatus.ACTIVE:
      return "#198282";
    case AdvisorStatus.PENDING:
      return "#FFA500";
    case AdvisorStatus.DEACTIVATED:
      return "#FF0000";
    case AdvisorStatus.STAFF:
    case AdvisorStatus.TEST:
    default:
      return "#9E9E9E";
  }
};

export default () => {
  const params = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [mapOpen, setMapOpen] = useState(false);
  const [addAdvisorOpen, setAddAdvisorOpen] = useState(false);
  const [inviteAdvisorOpen, setInviteAdvisorOpen] = useState(false);
  const [editAdvisor, setEditAdvisor] = useState<Advisor | null>(null);
  const [query, setQuery] = useState(
    new URLSearchParams(window.location.search).get("query") || "",
  );
  const { request, data, loading } = useAdminListAllAdvisors();
  const [, setSearchParams] = useSearchParams();
  const [initialAdvisorRef] = useState<string | undefined>(params.advisorRef);
  const [advisorRefs, setAdvisorRefs] = useState<string[]>(
    params.advisorRef ? [params.advisorRef] : [],
  );

  useEffect(() => {
    request();
  }, []);

  useEffect(() => {
    // Update URL whenever query changes
    setSearchParams(query ? { query } : {});
  }, [query, setSearchParams]);

  // Update URL when lead card is opened/closed
  const handleCardOpen = (ref: string) => {
    setAdvisorRefs([ref]);
    navigate(`/tools/advisor-administration/${encodeURIComponent(ref)}`);
  };

  const handleCardClose = () => {
    setAdvisorRefs([]);
    navigate(`/tools/advisor-administration`);
  };

  const allAdvisors = (data?.advisors || []).sort((a, b) =>
    a.displayName.localeCompare(b.displayName),
  );

  let resultingAdvisors = allAdvisors;
  if (query) {
    const cleanQuery = query.toLowerCase();
    resultingAdvisors = resultingAdvisors.filter(
      (a) =>
        a.displayName.toLowerCase().includes(cleanQuery) ||
        getSearchableLocation(a.address).includes(cleanQuery) ||
        a.families.some((f: FamilyStub) =>
          f.name.toLowerCase().includes(cleanQuery),
        ),
    );
  }
  const mappableAdvisors = resultingAdvisors.filter(
    (a) => !!getFormattedLocation(a.address),
  );
  // On initial load, we want to show the lead card for the lead that was referenced in the URL at the top.
  resultingAdvisors = [
    ...resultingAdvisors.filter((a) => initialAdvisorRef === a.ref),
    ...resultingAdvisors.filter((a) => initialAdvisorRef !== a.ref),
  ];

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "64px",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              {
                name: "Home",
                link: "/",
              },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Typography variant="display">Advisor Administration</Typography>
            <Box
              display="flex"
              flexDirection="row"
              gap="8px"
              alignItems={"center"}
            >
              <SearchBar
                initialQuery={query}
                fullWidth={isMobile}
                autofocus={true}
                onQuery={setQuery}
                onKeyPress={true}
              />
            </Box>
          </Box>
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            gap="12px"
            marginTop="12px"
            justifyContent={"space-between"}
          >
            <Box display="flex" flexDirection="row" gap="12px" marginTop="12px">
              <Button
                variant="outlined"
                onClick={() => setAddAdvisorOpen(true)}
                sx={{
                  height: "36px",
                  minWidth: "unset",
                  padding: "0 15px",
                }}
                startIcon={<Plus />}
              >
                Add Advisor
              </Button>
              <Button
                variant="outlined"
                onClick={() => setInviteAdvisorOpen(true)}
                sx={{
                  height: "36px",
                  minWidth: "unset",
                  padding: "0 15px",
                }}
                startIcon={
                  <MailCheck
                    style={{
                      height: "20px",
                      width: "20px",
                    }}
                  />
                }
              >
                Invite Advisor
              </Button>
            </Box>
            <Box display="flex" flexDirection="row" gap="12px" marginTop="12px">
              <Button
                onClick={() => mappableAdvisors.length && setMapOpen(true)}
                sx={{
                  borderRadius: "100px",
                  border: "2px solid #EAEBEC",
                  color: "#616161",
                  backgroundColor: "unset",
                  fontWeight: 500,
                  "&:hover": {
                    backgroundColor: "#EAEBEC",
                  },
                }}
                startIcon={<MapIcon size={20} stroke="#8E9598" />}
              >
                Mapped
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          gap: "16px",
          padding: !isMobile ? "0" : undefined,
        }}
      >
        {!isMobile && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="end"
            padding="24px 32px 0 32px"
            mr="34px"
            ml="60px"
          >
            <Box flex={5} display="flex">
              <Typography variant="bodySmallHeavy" color="#262626">
                Name
              </Typography>
            </Box>
            {!isMobile && (
              <>
                <Box flex={2} display="flex" justifyContent="end">
                  <Typography
                    variant="bodySmallHeavy"
                    color="#262626"
                    alignSelf={"end"}
                  >
                    Location
                  </Typography>
                </Box>
                <Box flex={2} display="flex" justifyContent="end">
                  <Typography variant="bodySmallHeavy" color="#262626">
                    Status
                  </Typography>
                </Box>{" "}
              </>
            )}
          </Box>
        )}
        {loading && <Loading />}
        {resultingAdvisors.map((advisor) => (
          <AdvisorCard
            key={advisor.ref}
            advisor={advisor}
            onEdit={() => setEditAdvisor(advisor)}
            forceOpen={
              resultingAdvisors.length === 1 ||
              advisorRefs.indexOf(advisor.ref) >= 0
            }
            onClose={() => handleCardClose()}
            onOpen={() => handleCardOpen(advisor.ref)}
          />
        ))}
      </Box>
      <InviteAdvisorDialog
        open={inviteAdvisorOpen}
        onClose={() => {
          setInviteAdvisorOpen(false);
        }}
      />
      {editAdvisor && (
        <EditAdvisorDialog
          advisor={editAdvisor}
          open={!!editAdvisor}
          onClose={(updated) => {
            setEditAdvisor(null);
            if (updated) {
              request();
            }
          }}
        />
      )}
      <AddAdvisorDialog
        open={addAdvisorOpen}
        onClose={(created) => {
          setAddAdvisorOpen(false);
          if (created) {
            request();
          }
        }}
      />
      <MapList
        open={mapOpen && mappableAdvisors.length > 0}
        onClose={() => setMapOpen(false)}
        locations={resultingAdvisors
          .filter((a) => a.status !== AdvisorStatus.DEACTIVATED)
          .map((a) => ({
            title: a.displayName,
            link: `/advisor/${encodeURIComponent(a.ref)}`,
            content: getFormattedLocation(a.address),
            color: getStateColor(a.status),
            treatment: Treatment.Pin,
            bounding: true,
            address: getFormattedLocation(a.address),
            ...getLatLng(a.address),
          }))}
      />
    </Box>
  );
};
